import { html, nothing } from 'lit';
import type { LandingContent, LandingGlobusCampaignHeroContent } from '../../shared/content/landingContent.js';
import { editorSemanticText } from '../editor/editorSemanticText.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';
import { openCompanyContactDialog } from './navigation.js';

type GlobusCampaignHeroKey = 'globusCampaignApplicant' | 'globusCampaignCompany';

const mediaCardHoverTimers = new WeakMap<HTMLElement, number>();

const resetMediaCardTilt = (card: HTMLButtonElement) => {
  card.style.setProperty('--card-tilt-x', '0deg');
  card.style.setProperty('--card-tilt-y', '0deg');
  card.style.setProperty('--card-shine-x', '50%');
  card.style.setProperty('--card-shine-y', '50%');
};

const setActiveMediaCard = (card: HTMLButtonElement) => {
  const stack = card.closest<HTMLElement>('.vp-globus-campaign-wallet');
  if (!stack) return;
  stack.querySelectorAll<HTMLButtonElement>('.vp-globus-campaign-media-card').forEach(item => {
    const isActive = item === card;
    item.classList.toggle('is-active', isActive);
    item.setAttribute('aria-pressed', String(isActive));
    if (!isActive) resetMediaCardTilt(item);
  });
};

const setMediaCardTilt = (card: HTMLButtonElement, clientX: number, clientY: number) => {
  if (!card.classList.contains('is-active')) return;
  const bounds = card.getBoundingClientRect();
  const relativeX = Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width));
  const relativeY = Math.min(1, Math.max(0, (clientY - bounds.top) / bounds.height));
  card.style.setProperty('--card-tilt-x', `${((0.5 - relativeY) * 7).toFixed(2)}deg`);
  card.style.setProperty('--card-tilt-y', `${((relativeX - 0.5) * 9).toFixed(2)}deg`);
  card.style.setProperty('--card-shine-x', `${(relativeX * 100).toFixed(1)}%`);
  card.style.setProperty('--card-shine-y', `${(relativeY * 100).toFixed(1)}%`);
};

const updateMediaCardTilt = (event: PointerEvent) => {
  if (event.pointerType !== 'mouse') return;
  setMediaCardTilt(event.currentTarget as HTMLButtonElement, event.clientX, event.clientY);
};

const queueMediaCardActivation = (event: PointerEvent) => {
  if (event.pointerType !== 'mouse') return;
  const card = event.currentTarget as HTMLButtonElement;
  const stack = card.closest<HTMLElement>('.vp-globus-campaign-wallet');
  if (!stack) return;
  const pendingTimer = mediaCardHoverTimers.get(stack);
  if (pendingTimer !== undefined) window.clearTimeout(pendingTimer);
  const pointerX = event.clientX;
  const pointerY = event.clientY;
  const nextTimer = window.setTimeout(() => {
    if (!card.matches(':hover')) return;
    setActiveMediaCard(card);
    setMediaCardTilt(card, pointerX, pointerY);
  }, 160);
  mediaCardHoverTimers.set(stack, nextTimer);
};

const leaveMediaCard = (event: PointerEvent) => {
  const card = event.currentTarget as HTMLButtonElement;
  const stack = card.closest<HTMLElement>('.vp-globus-campaign-wallet');
  const pendingTimer = stack ? mediaCardHoverTimers.get(stack) : undefined;
  if (pendingTimer !== undefined) window.clearTimeout(pendingTimer);
  resetMediaCardTilt(card);
};

// Touch selections stay put; the short mouse-hover intent keeps the compact fan from swapping layers accidentally.
const activateMediaCard = (event: Event) => setActiveMediaCard(event.currentTarget as HTMLButtonElement);

const editableText = (value: string, elementId: string, className: string, editor?: LandingEditorRenderOptions) => html`
  <span
    class=${editorNodeClass(editor, elementId, className)}
    style=${editorNodeStyle(editor, elementId)}
    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, elementId, event)}
    @click=${(event: MouseEvent) => editorNodeClick(editor, elementId, event)}
    >${value}</span
  >
`;

const renderCampaignAction = (
  hero: LandingGlobusCampaignHeroContent,
  key: GlobusCampaignHeroKey,
  action: 'primaryAction' | 'secondaryAction',
  editor?: LandingEditorRenderOptions,
) => {
  const content = hero[action];
  const isPrimary = action === 'primaryAction';
  const id = `${key}.${action}`;

  return html`
    <a
      class=${editorNodeClass(
        editor,
        `${id}.label`,
        `vp-action ${isPrimary ? 'vp-action-primary' : 'vp-action-secondary'} vp-globus-campaign-action`,
      )}
      style=${editorNodeStyle(editor, `${id}.label`)}
      href=${content.href}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${id}.label`, event)}
      @click=${(event: MouseEvent) => {
        if (editor?.mode === 'editor') {
          event.preventDefault();
          editorNodeClick(editor, `${id}.label`, event);
          return;
        }
        if (key === 'globusCampaignCompany' && content.href === '#fachkraefte-anfragen') {
          event.preventDefault();
          openCompanyContactDialog((event.currentTarget as HTMLElement).closest('.vp-page'));
        }
      }}
    >
      ${content.label}
      ${isPrimary
        ? html`<span class="icon-[material-symbols--arrow-forward-rounded]" aria-hidden="true"></span>`
        : nothing}
    </a>
  `;
};

export const globusCampaignHeroSection = (
  key: GlobusCampaignHeroKey,
  content: LandingContent,
  editor?: LandingEditorRenderOptions,
) => {
  const hero = content[key];
  if (!hero) return nothing;

  return html`
    <section
      class=${editorNodeClass(
        editor,
        `section.${key}`,
        `vp-globus-campaign vp-globus-campaign--${key === 'globusCampaignCompany' ? 'company' : 'applicant'}`,
      )}
      style=${editorNodeStyle(editor, `section.${key}`)}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `section.${key}`, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, `section.${key}`, event)}
    >
      <div class="vp-shell vp-globus-campaign-shell">
        <div
          class=${editorNodeClass(editor, `${key}.copy`, 'vp-globus-campaign-copy')}
          style=${editorNodeStyle(editor, `${key}.copy`)}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.copy`, event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.copy`, event)}
        >
          ${editorSemanticText({
            editor,
            elementId: `${key}.title`,
            fallbackTag: 'h1',
            className: 'vp-globus-campaign-title',
            content: hero.title,
          })}
          ${editorSemanticText({
            editor,
            elementId: `${key}.intro`,
            fallbackTag: 'p',
            className: 'vp-globus-campaign-intro',
            content: hero.intro,
          })}
          <div class="vp-globus-campaign-actions">
            ${renderCampaignAction(hero, key, 'primaryAction', editor)}
            ${key === 'globusCampaignCompany' ? renderCampaignAction(hero, key, 'secondaryAction', editor) : nothing}
          </div>
        </div>

        <div
          class=${editorNodeClass(editor, `${key}.media`, 'vp-globus-campaign-media')}
          style=${editorNodeStyle(editor, `${key}.media`)}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.media`, event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.media`, event)}
        >
          <div
            class="vp-globus-campaign-wallet"
            role="group"
            aria-label=${content.locale === 'tr'
              ? 'GLOBUS çalışma alanlarından görüntüler'
              : content.locale === 'en'
                ? 'Images from GLOBUS work areas'
                : 'Einblicke in die GLOBUS Arbeitsbereiche'}
          >
            ${hero.mediaCards.map(
              (card, index) => html`
                <button
                  class=${editorNodeClass(editor, `${key}.mediaCard.${index}`, 'vp-globus-campaign-media-card')}
                  style=${editorNodeStyle(editor, `${key}.mediaCard.${index}`)}
                  type="button"
                  aria-label=${card.imageAlt}
                  aria-pressed="false"
                  @pointerdown=${(event: PointerEvent) =>
                    editorNodePointerDown(editor, `${key}.mediaCard.${index}`, event)}
                  @pointerenter=${(event: PointerEvent) => {
                    if (editor?.mode !== 'editor') queueMediaCardActivation(event);
                  }}
                  @pointermove=${(event: PointerEvent) => {
                    if (editor?.mode !== 'editor') updateMediaCardTilt(event);
                  }}
                  @pointerleave=${(event: PointerEvent) => {
                    if (editor?.mode !== 'editor') leaveMediaCard(event);
                  }}
                  @focus=${(event: FocusEvent) => {
                    if (editor?.mode !== 'editor') setActiveMediaCard(event.currentTarget as HTMLButtonElement);
                  }}
                  @blur=${(event: FocusEvent) => resetMediaCardTilt(event.currentTarget as HTMLButtonElement)}
                  @click=${(event: MouseEvent) => {
                    if (editor?.mode === 'editor') {
                      editorNodeClick(editor, `${key}.mediaCard.${index}`, event);
                      return;
                    }
                    activateMediaCard(event);
                  }}
                >
                  <span class="vp-globus-campaign-media-card-surface">
                    <img
                      class=${editorNodeClass(
                        editor,
                        `${key}.mediaCard.${index}.image`,
                        'vp-globus-campaign-media-card-image',
                      )}
                      style=${editorNodeStyle(editor, `${key}.mediaCard.${index}.image`)}
                      src=${card.image}
                      alt=${card.imageAlt}
                      loading=${index === hero.mediaCards.length - 1 ? 'eager' : 'lazy'}
                      fetchpriority=${index === hero.mediaCards.length - 1 ? 'high' : 'auto'}
                      @pointerdown=${(event: PointerEvent) =>
                        editorNodePointerDown(editor, `${key}.mediaCard.${index}.image`, event)}
                      @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.mediaCard.${index}.image`, event)}
                    />
                  </span>
                </button>
              `,
            )}
          </div>
        </div>

        <!-- Native links keep every vertically stacked mobile job card keyboard- and touch-accessible. -->
        <nav
          class=${editorNodeClass(editor, `${key}.cards`, 'vp-globus-campaign-cards')}
          style=${editorNodeStyle(editor, `${key}.cards`)}
          aria-label=${content.locale === 'tr'
            ? 'GLOBUS çalışma alanları'
            : content.locale === 'en'
              ? 'GLOBUS work areas'
              : 'GLOBUS Arbeitsbereiche'}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.cards`, event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.cards`, event)}
        >
          ${hero.cards.map(
            (card, index) => html`
              <a
                class=${editorNodeClass(editor, `${key}.card.${index}`, 'vp-globus-campaign-card')}
                style=${editorNodeStyle(editor, `${key}.card.${index}`)}
                href=${card.href}
                aria-label=${card.title}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.card.${index}`, event)}
                @click=${(event: MouseEvent) => {
                  if (editor?.mode !== 'editor') return;
                  event.preventDefault();
                  editorNodeClick(editor, `${key}.card.${index}`, event);
                }}
              >
                <img
                  class=${editorNodeClass(editor, `${key}.card.${index}.image`, 'vp-globus-campaign-card-image')}
                  style=${editorNodeStyle(editor, `${key}.card.${index}.image`)}
                  src=${card.image}
                  alt=${card.imageAlt}
                  loading="lazy"
                  @pointerdown=${(event: PointerEvent) =>
                    editorNodePointerDown(editor, `${key}.card.${index}.image`, event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.card.${index}.image`, event)}
                />
                ${editableText(card.title, `${key}.card.${index}.title`, 'vp-globus-campaign-card-title', editor)}
                <span
                  class="vp-globus-campaign-card-arrow icon-[material-symbols--arrow-outward-rounded]"
                  aria-hidden="true"
                ></span>
              </a>
            `,
          )}
        </nav>
      </div>
    </section>
  `;
};
